import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import type { Asset, SearchResponse } from "@chartandgo/shared";
import { searchSymbols } from "../api/market.js";
import { useDebouncedValue } from "../hooks/useDebouncedValue.js";
import type { CardPlacement } from "../store/workspace.js";
import { SearchIcon } from "./Icons.js";

interface SymbolPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (asset: Asset) => void;
  placement?: CardPlacement;
}

export function SymbolPicker({
  open,
  onClose,
  onSelect,
  placement,
}: SymbolPickerProps) {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const debouncedQuery = useDebouncedValue(query.trim(), 200);

  const { data, isFetching, error } = useQuery<SearchResponse>({
    queryKey: ["search", debouncedQuery],
    queryFn: ({ signal }) => searchSymbols(debouncedQuery, signal),
    enabled: open && debouncedQuery.length > 0,
    staleTime: 60_000,
    retry: false,
  });

  const results = data?.results ?? [];

  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
      // Focus after the modal paints.
      const id = requestAnimationFrame(() => inputRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [debouncedQuery]);

  if (!open) return null;

  const handleSelect = (asset: Asset | undefined) => {
    if (!asset) return;
    onSelect(asset);
    onClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      handleSelect(results[activeIndex]);
    }
  };

  return (
    <div
      className="picker-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="picker"
        role="dialog"
        aria-modal="true"
        aria-label="Add a chart"
        onKeyDown={handleKeyDown}
      >
        {placement && (
          <div className="picker__placement">
            <span className="picker__placement-mark" aria-hidden />
            <div>
              <strong>
                {"x" in placement ? "Position selected" : "Size selected"}
              </strong>
              <span>
                {"x" in placement
                  ? `${placement.w} × ${placement.h} grid rectangle`
                  : `${placement.w} × ${placement.h} chart · next open space`}
              </span>
            </div>
          </div>
        )}
        <div className="picker__search">
          <SearchIcon className="picker__search-icon" />
          <input
            ref={inputRef}
            className="picker__input"
            type="text"
            placeholder="Search by symbol or company name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search symbols"
            autoComplete="off"
            spellCheck={false}
          />
          {isFetching && <span className="picker__spinner" aria-hidden />}
        </div>

        <div className="picker__results" role="listbox">
          {error && (
            <div className="picker__empty picker__empty--error">
              {(error as Error).message}
            </div>
          )}

          {!error && debouncedQuery.length === 0 && (
            <div className="picker__empty">
              Start typing to find a stock or ETF.
            </div>
          )}

          {!error && debouncedQuery.length > 0 && results.length === 0 && !isFetching && (
            <div className="picker__empty">
              No matches for “{debouncedQuery}”.
            </div>
          )}

          {results.map((asset, index) => (
            <button
              key={asset.symbol}
              type="button"
              role="option"
              aria-selected={index === activeIndex}
              className={`picker__result ${
                index === activeIndex ? "picker__result--active" : ""
              }`}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => handleSelect(asset)}
            >
              <span className="picker__symbol">{asset.symbol}</span>
              <span className="picker__name">{asset.name}</span>
              <span className="picker__exchange">{asset.exchange}</span>
            </button>
          ))}
        </div>

        <div className="picker__footer">
          <kbd>↑</kbd>
          <kbd>↓</kbd>
          <span>to navigate</span>
          <kbd>↵</kbd>
          <span>to add</span>
          <kbd>esc</kbd>
          <span>to close</span>
        </div>
      </div>
    </div>
  );
}
